import React from "react";
import { DebuggerSession } from "../../types/debuggerServiceTypes";
import { formatDateTime } from "../../utils/textUtils";

interface SessionListProps {
  sessions: DebuggerSession[];
  selectedSession: DebuggerSession | null;
  onSessionSelect: (session: DebuggerSession) => void;
}

const SessionList: React.FC<SessionListProps> = ({
  sessions,
  selectedSession,
  onSessionSelect,
}) => {
  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  if (sessions.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          No sessions found
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          There are no sessions to display.
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200 h-full overflow-y-auto min-h-0">
      {sessions.map((session) => (
        <div
          key={`${session.session_id}-${session.session_create_time}`}
          className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
            selectedSession?.session_id === session.session_id
              ? "bg-blue-50 border-l-4 border-blue-400"
              : ""
          }`}
          onClick={() => onSessionSelect(session)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              {/* Session ID */}
              <div className="flex items-center space-x-2">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {session.session_id.substring(0, 8)}...
                </p>

                {/* Flagged indicator */}
                {session.is_flagged && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                    🚩 Flagged
                    {session.flagged_message_query_ids &&
                      session.flagged_message_query_ids.length > 1 && (
                        <span className="ml-1 text-xs">
                          ({session.flagged_message_query_ids.length} messages)
                        </span>
                      )}
                  </span>
                )}

                {/* Reviewed indicator */}
                {session.has_session_been_reviewed && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    ✓ Reviewed
                  </span>
                )}
              </div>

              {/* User info */}
              <div className="mt-1 text-sm text-gray-500">
                {session.user_email && (
                  <p className="truncate">{session.user_email}</p>
                )}
              </div>

              {/* Timestamps */}
              <div className="mt-2 text-xs text-gray-400">
                <p>Started: {formatDateTime(session.session_create_time)}</p>
              </div>

              {/* User feedback preview for flagged sessions */}
              {session.is_flagged &&
                session.flagged_msg_feedback &&
                session.flagged_msg_feedback.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs font-medium text-orange-700 mb-1 flex items-center">
                      <span className="mr-1">💬</span>
                      User Feedback:
                    </div>
                    <div className="space-y-1">
                      {session.flagged_msg_feedback
                        .slice(0, 2)
                        .map((feedback, index) => (
                          <div
                            key={index}
                            className="text-xs text-orange-700 bg-orange-50 px-2 py-1 rounded border border-orange-200 leading-relaxed"
                          >
                            "{truncateText(feedback, 80)}"
                          </div>
                        ))}
                      {session.flagged_msg_feedback.length > 2 && (
                        <div className="text-xs text-orange-500 italic">
                          +{session.flagged_msg_feedback.length - 2} more
                          feedback items
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SessionList;
